import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { DateFormatService } from '../../../../core/services/date-format.service';
import { ToastService } from '../../../../core/services/toast.service';

import {
  ClientCaseApiModel,
  ClientCaseSummaryCounts,
  ClientDetailApiModel,
} from '../../models/client.models';
import { ClientsService } from '../../services/clients.service';
import {
  BreadcrumbItem,
  BreadcrumbsComponent,
} from '../../../../shared/components/breadcrumbs/breadcrumbs.component';

type ClientCasesTab = 'open' | 'history';

export interface ClientCaseSummary {
  id: number;
  caseUniqueId: string;
  status: 'open' | 'closed' | 'declined_closed';
  serviceName: string;
  partnerName: string;
  priority: 'low' | 'medium' | 'high';
  referralSource: string;
  referralDate: string;
  dateOpened: string;
  closeDate: string | null;
}

export interface ClientDetailVm {
  id: number;
  clientUniqueId: string | null;
  fullName: string;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  birthdate: string;
  status: 'open' | 'closed';
}

@Component({
  selector: 'app-client-detail',
  standalone: true,
  imports: [CommonModule, BreadcrumbsComponent],
  templateUrl: './client-detail.component.html',
  styleUrl: './client-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientDetailComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly clientsService = inject(ClientsService);
  private readonly authService = inject(AuthService);
  private readonly dateFormatService = inject(DateFormatService);
  private readonly toastService = inject(ToastService);

  readonly clientId = Number(this.route.snapshot.paramMap.get('id'));

  readonly clientLoading = signal(true);
  readonly casesLoading = signal(false);
  readonly statusUpdating = signal(false);
  readonly pendingStatusChange = signal<'closed' | null>(null);
  readonly errorMessage = signal<string | null>(null);

  readonly activeTab = signal<ClientCasesTab>('open');
  readonly client = signal<ClientDetailVm | null>(null);

  readonly openCases = signal<ClientCaseSummary[]>([]);
  readonly closedCases = signal<ClientCaseSummary[]>([]);

  readonly openCasesTotal = signal(0);
  readonly closedCasesTotal = signal(0);

  readonly openCasesLoaded = signal(false);
  readonly closedCasesLoaded = signal(false);

  readonly displayedCases = computed(() =>
    this.activeTab() === 'open' ? this.openCases() : this.closedCases(),
  );

  readonly breadcrumbItems = computed<BreadcrumbItem[]>(() => {
    const currentClient = this.client();
    const label = currentClient?.fullName || 'Client';

    return [
      { label: 'Home', route: '/home' },
      { label: 'Clients' },
      { label },
    ];
  });

  readonly caseSummary = signal<ClientCaseSummaryCounts>({ open: 0, closed: 0, total: 0 });

  readonly openCaseCount = computed(() => this.openCasesTotal());
  readonly closedCaseCount = computed(() => this.closedCasesTotal());
  readonly canToggleClientStatus = computed(() => {
    const role = this.authService.role();
    return role === 'admin' || role === 'dekalb';
  });

  constructor() {
    this.loadClient();
  }

  loadClient(): void {
    if (!this.clientId || Number.isNaN(this.clientId)) {
      this.errorMessage.set('Invalid client reference.');
      this.clientLoading.set(false);
      return;
    }

    this.clientLoading.set(true);
    this.errorMessage.set(null);

    this.clientsService
      .getClientDetail(this.clientId)
      .pipe(finalize(() => this.clientLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.caseSummary.set(response.caseSummary);
          this.openCasesTotal.set(response.caseSummary.open);
          this.closedCasesTotal.set(response.caseSummary.closed);
          this.client.set(this.toClientDetailVm(response.client));
          this.loadCases('open');
        },
        error: (error) => {
          this.errorMessage.set(
            error?.error?.message ?? 'Unable to load client details right now.',
          );
        },
      });
  }

  loadCases(status: ClientCasesTab, force = false): void {
    if (status === 'open' && this.openCasesLoaded() && !force) {
      return;
    }

    if (status === 'history' && this.closedCasesLoaded() && !force) {
      return;
    }

    this.casesLoading.set(true);
    this.errorMessage.set(null);

    const apiStatus = status === 'history' ? 'closed' : 'open';

    this.clientsService
      .getClientCases(this.clientId, apiStatus)
      .pipe(finalize(() => this.casesLoading.set(false)))
      .subscribe({
        next: (response) => {
          const mappedCases = response.data.map((item) => this.toClientCaseSummary(item));

          if (status === 'open') {
            this.openCases.set(mappedCases);
            this.openCasesTotal.set(this.caseSummary().open);
            this.openCasesLoaded.set(true);
          } else {
            this.closedCases.set(mappedCases);
            this.closedCasesTotal.set(this.caseSummary().closed);
            this.closedCasesLoaded.set(true);
          }
        },
        error: (error) => {
          this.errorMessage.set(
            error?.error?.message ?? 'Unable to load client cases right now.',
          );
        },
      });
  }

  setTab(tab: ClientCasesTab): void {
    if (this.activeTab() === tab) {
      return;
    }

    this.activeTab.set(tab);
    this.loadCases(tab);
  }

  goBackHome(): void {
    void this.router.navigate(['/home']);
  }

  openProfile(): void {
    void this.router.navigate(['/clients', this.clientId, 'profile']);
  }

  createCase(): void {
    void this.router.navigate(['/clients', this.clientId, 'case']);
  }

  setClientStatus(nextStatus: 'open' | 'closed'): void {
    if (!this.canToggleClientStatus()) {
      return;
    }

    const current = this.client();
    if (!current || this.statusUpdating()) {
      return;
    }

    if (current.status === nextStatus) {
      return;
    }

    if (nextStatus === 'closed') {
      this.pendingStatusChange.set('closed');
      return;
    }

    this.applyClientStatus(nextStatus);
  }

  confirmCloseClient(): void {
    if (this.statusUpdating() || this.pendingStatusChange() !== 'closed') {
      return;
    }

    this.pendingStatusChange.set(null);
    this.applyClientStatus('closed');
  }

  cancelCloseClient(): void {
    if (this.statusUpdating()) {
      return;
    }

    this.pendingStatusChange.set(null);
  }

  private applyClientStatus(nextStatus: 'open' | 'closed'): void {
    const current = this.client();
    if (!current || this.statusUpdating()) {
      return;
    }

    if (current.status === nextStatus) {
      return;
    }

    this.statusUpdating.set(true);
    this.clientsService
      .updateClientStatus(this.clientId, { status: nextStatus })
      .pipe(finalize(() => this.statusUpdating.set(false)))
      .subscribe({
        next: ({ client }) => {
          this.client.set(this.toClientDetailVm(client));
          this.toastService.success(
            'Client Status Updated',
            client.status === 'open' ? 'Client status set to Open.' : 'Client status set to Closed.',
          );
        },
        error: (error) => {
          this.toastService.danger(
            'Update Failed',
            error?.error?.message ?? 'Unable to update client status right now.',
          );
        },
      });
  }

  openCase(caseId: number): void {
    void this.router.navigate(['/clients', this.clientId, 'case', caseId]);
  }

  copyClientUniqueId(): void {
    const uniqueId = this.client()?.clientUniqueId?.trim();
    if (!uniqueId) {
      this.toastService.danger('Copy Failed', 'Client ID is not available.');
      return;
    }

    if (!('clipboard' in navigator) || typeof navigator.clipboard?.writeText !== 'function') {
      this.toastService.danger('Copy Failed', 'Clipboard is not supported in this browser.');
      return;
    }

    void navigator.clipboard
      .writeText(uniqueId)
      .then(() => {
        this.toastService.success('Client ID Copied', uniqueId);
      })
      .catch(() => {
        this.toastService.danger('Copy Failed', 'Unable to copy Client ID right now.');
      });
  }

  trackCaseById(_index: number, item: ClientCaseSummary): number {
    return item.id;
  }

  private toClientDetailVm(client: ClientDetailApiModel): ClientDetailVm {
    const nameParts = [client.firstName, client.middleName, client.lastName]
      .map((part) => (part ?? '').trim())
      .filter(Boolean);

    return {
      id: client.id,
      clientUniqueId: client.clientUniqueId ?? null,
      fullName: nameParts.join(' ') || 'Client',
      firstName: client.firstName,
      middleName: client.middleName,
      lastName: client.lastName,
      birthdate: client.birthdate ?? 'Unknown',
      status: client.status,
    };
  }

  getCaseStatusLabel(status: ClientCaseSummary['status']): string {
    if (status === 'declined_closed') {
      return 'Declined/Closed';
    }

    return status === 'open' ? 'Open' : 'Closed';
  }

  private toClientCaseSummary(item: ClientCaseApiModel): ClientCaseSummary {
    return {
      id: item.id,
      caseUniqueId: item.caseUniqueId,
      status: item.status,
      serviceName: item.serviceName?.trim() || 'Unassigned service',
      partnerName: item.partnerName?.trim() || 'Unassigned partner',
      priority: item.priority,
      referralSource: this.formatReferralSource(item.referralSource, item.referralSourceOther),
      referralDate: this.formatDate(item.referralDate),
      dateOpened: this.formatDate(item.openedAt),
      closeDate: item.closedAt ? this.formatDate(item.closedAt) : null,
    };
  }

  private formatReferralSource(
    source: ClientCaseApiModel['referralSource'],
    other: string | null,
  ): string {
    if (source === 'community_based_organization') {
      return 'Community Based Organization';
    }

    if (source === 'other') {
      const detail = (other ?? '').trim();
      return detail ? `Other (${detail})` : 'Other';
    }

    return source.charAt(0).toUpperCase() + source.slice(1);
  }

  private formatDate(value: string): string {
    return this.dateFormatService.formatDate(value, value);
  }
}
